import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { workProjects } from '../data/mock';

const Work = () => {
  const categories = ['All', ...new Set(workProjects.map(p => p.category))];
  const [activeCategory, setActiveCategory] = useState('All');

  const filteredProjects = activeCategory === 'All' 
    ? workProjects 
    : workProjects.filter(p => p.category === activeCategory);

  return (
    <div className="bg-black pt-24 lg:pt-32">
      {/* Hero Section */}
      <section className="py-14 md:py-24 lg:py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          {/* Overline */}
          <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-white/40 mb-6">
            Portfolio
          </p>

          {/* Headline */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-end">
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-white font-normal leading-[0.95] tracking-tight">
              Selected Work
            </h1>
            <p className="font-sans text-base text-white/50 max-w-md leading-relaxed font-light lg:pb-2">
              A curated collection of strategic projects spanning cultural institutions, 
              global corporations, and governmental bodies.
            </p>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-6 mt-16 pb-8 border-b border-white/10">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`font-sans text-[11px] tracking-[0.15em] uppercase transition-all duration-300 ${
                  activeCategory === category
                    ? 'text-white'
                    : 'text-white/40 hover:text-white/70'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects */}
      <section className="pb-14 md:pb-24 lg:pb-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="space-y-20 lg:space-y-32">
            {filteredProjects.map((project, index) => (
              <article
                key={project.id}
                className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center"
              >
                {/* Image */}
                <div className={`lg:col-span-7 ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full aspect-[16/10] object-cover grayscale hover:grayscale-0 transition-all duration-700"
                    />
                  </div>
                </div>

                {/* Content */}
                <div className={`lg:col-span-5 ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <span className="font-serif text-6xl lg:text-7xl text-white/10 block mb-4">
                    0{index + 1}
                  </span>
                  <p className="font-sans text-[11px] tracking-[0.2em] uppercase text-white/40 mb-3">
                    {project.category}
                  </p>
                  <h2 className="font-serif text-3xl lg:text-4xl text-white mb-3 tracking-tight">
                    {project.title}
                  </h2>
                  <p className="font-sans text-sm text-white/40 mb-6">
                    {project.client} · {project.year}
                  </p>
                  <p className="font-sans text-base text-white/60 leading-relaxed font-light">
                    {project.description}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-14 md:py-24 lg:py-24 bg-[#F8F6F3]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <div>
              <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-black/40 mb-4">
                Approach
              </p>
              <h2 className="font-serif text-4xl md:text-5xl text-black font-normal tracking-tight">
                The Process
              </h2>
            </div>
            <div className="space-y-0">
              {[
                { step: '01', title: 'Discover', desc: 'Deep listening and cultural research to understand context, stakeholders, and aspirations.' },
                { step: '02', title: 'Define', desc: 'Strategic framing that connects cultural intelligence with organizational objectives.' },
                { step: '03', title: 'Design', desc: 'Crafting narratives and frameworks that resonate authentically across audiences.' },
                { step: '04', title: 'Deliver', desc: 'Implementation with precision, ensuring lasting impact and measurable outcomes.' }
              ].map((item) => (
                <div key={item.step} className="flex gap-8 py-8 border-b border-black/10 last:border-b-0">
                  <span className="font-serif text-2xl text-black/20">{item.step}</span>
                  <div>
                    <h3 className="font-serif text-xl text-black tracking-tight">{item.title}</h3>
                    <p className="font-sans text-sm text-black/50 mt-2 leading-relaxed font-light">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-14 md:py-24 lg:py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white font-normal tracking-tight">
            Have a Project in Mind?
          </h2>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center mt-12 px-12 py-5 bg-gradient-to-r from-[#7B1818] to-[#4A0E0E] text-white font-sans text-[11px] tracking-[0.2em] uppercase hover:from-[#8B2020] hover:to-[#5A1010] transition-all duration-300"
          >
            Start a Conversation
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Work;
